import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Star, ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/site/SEO";
import Footer from "@/components/site/Footer";
import ConsentGate from "@/components/site/ConsentGate";
import { CALENDLY_URL } from "@/lib/constants";
import { LEAD_WEBHOOK_URL } from "@/lib/leadWebhook";
import logoWhite from "@/assets/finable-logo-white.svg";

/* ---------------------------------- data --------------------------------- */

const anchors = [
  { href: "#das-problem", label: "Das Problem" },
  { href: "#die-rechnung", label: "Die Rechnung" },
  { href: "#faq", label: "FAQ" },
];

const problems = [
  {
    n: "01",
    title: "Die gesetzliche Rente reicht nicht",
    text: "Die gesetzliche Rente ist als Grundversorgung angelegt, nicht als vollständiger Ersatz des letzten Einkommens. Für die meisten Arbeitnehmer bleibt zwischen letztem Gehalt und späterer Rente eine Lücke, die nur privat oder betrieblich geschlossen werden kann.",
  },
  {
    n: "02",
    title: "Förderungen bleiben liegen",
    text: "Zulagen, steuerliche Vorteile und Arbeitgeberzuschüsse stehen vielen Arbeitnehmern zu — ausgezahlt werden sie aber nur, wenn sie beantragt und richtig gestaltet werden. Ohne aktive Entscheidung passiert schlicht nichts.",
  },
  {
    n: "03",
    title: "Ungefördert sparen kostet doppelt",
    text: "Wer ausschließlich aus dem Netto spart und die Erträge voll versteuert, verliert gegenüber geförderten Wegen an Wirkung — einmal beim Einzahlen, einmal beim Ertrag. Bei gleicher monatlicher Rate kann das über Jahrzehnte einen spürbaren Unterschied machen.",
  },
  {
    n: "04",
    title: "Jedes Jahr Warten kostet",
    text: "Zulagen und steuerliche Vorteile gibt es in der Regel nicht rückwirkend. Ein verstrichenes Jahr ist Fördervolumen, das sich später nicht nachholen lässt — zusätzlich fehlt die Zeit, in der das Geld hätte arbeiten können.",
  },
];

const steps = [
  {
    n: "1",
    title: "Kurzes Analysegespräch",
    text: "20 Minuten per Video oder Telefon. Wir schauen uns deine aktuelle Situation an und klären, welche Wege für dich überhaupt in Frage kommen.",
  },
  {
    n: "2",
    title: "Beratungsgespräch",
    text: "Du bekommst dein persönliches Förder-Konzept — verständlich erklärt, mit den Zahlen, die für dich gelten, und ohne Fachchinesisch.",
  },
  {
    n: "3",
    title: "Umsetzung",
    text: "Wenn du dich dafür entscheidest, kümmern wir uns um Anträge und Einrichtung. Du musst nichts selbst beantragen.",
  },
];

const quotes = [
  {
    name: "Julia",
    age: 27,
    city: "Hamburg",
    profession: "Personalentwicklerin",
    quote:
      "Mit FINABLE habe ich eine Strategie, die wirklich zu mir passt – renditeorientiert und zukunftssicher.",
  },
  {
    name: "Moritz",
    age: 25,
    city: "Kiel",
    profession: "Orthopädietechnikmeister",
    quote:
      "Transparent, sympathisch und auf Augenhöhe – so habe ich Finanzberatung noch nie erlebt.",
  },
  {
    name: "Anja",
    age: 27,
    city: "München",
    profession: "Performance-Marketing-Managerin",
    quote:
      "FINABLE hat mir endlich Klarheit über meine Finanzen gegeben – ich weiß jetzt genau, wo mein Geld arbeitet.",
  },
];

const videos = ["1058336344", "1058327664"];

const faqs = [
  {
    q: "Was kostet die Beratung?",
    a: "Das Erstgespräch ist kostenfrei und unverbindlich. Wenn daraus eine Zusammenarbeit wird, besprechen wir vorher transparent, wie die Vergütung aussieht.",
  },
  {
    q: "Muss ich etwas abschließen?",
    a: "Nein. Im Erstgespräch wird nichts verkauft. Du entscheidest danach in Ruhe, ob und was du umsetzen möchtest.",
  },
  {
    q: "Für wen lohnt sich das?",
    a: "Vor allem für Arbeitnehmer und Angestellte, die regelmäßig etwas zurücklegen oder damit anfangen wollen und wissen möchten, welche Förderwege für sie in Frage kommen.",
  },
  {
    q: "Welche Förderungen gibt es überhaupt?",
    a: "Je nach Situation kommen betriebliche Altersvorsorge inklusive Arbeitgeberzuschuss, staatlich geförderte private Vorsorgeformen und steuerlich abzugsfähige Beiträge in Betracht. Welche davon für dich sinnvoll sind, hängt von Einkommen, Familienstand und Zielen ab — genau das rechnen wir im Gespräch aus.",
  },
  {
    q: "Wie läuft das Erstgespräch ab?",
    a: "Rund 20 Minuten per Video oder Telefon. Wir stellen dir ein paar Fragen zu Einkommen, bisheriger Vorsorge und Zielen und sagen dir ehrlich, ob wir dir weiterhelfen können.",
  },
  {
    q: "Seid ihr unabhängig?",
    a: "Wir sind unabhängige Vermittler nach § 34d und § 34f GewO, nicht an eine Bank oder einen bestimmten Anbieter gebunden und arbeiten ohne eigene Produkte.",
  },
  {
    q: "Wie schnell bekomme ich mein Ergebnis?",
    a: "In der Regel im Beratungsgespräch selbst — meist innerhalb weniger Tage nach dem Analysegespräch.",
  },
];

/* ---------------------------------- form ---------------------------------- */

const savingOptions = ["ETF / Depot", "Versicherung", "Tagesgeld / gar nicht", "Weiß nicht"];
const rateOptions = ["unter 100 €", "100–300 €", "über 300 €"];

function LeadForm() {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState("");
  const [rate, setRate] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      quelle: "Landingpage /deine-foerderanalyse",
      sparform: saving,
      sparrate: rate,
      name,
      email,
      telefon: phone,
      timestamp: new Date().toISOString(),
    };
    if (LEAD_WEBHOOK_URL) {
      try {
        await fetch(LEAD_WEBHOOK_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "no-cors",
          body: JSON.stringify(payload),
        });
      } catch {
        /* no-cors: Ergebnis nicht lesbar */
      }
      setBusy(false);
      setSent(true);
    } else {
      // Übergangslösung, solange kein Webhook hinterlegt ist: direkt zu Calendly.
      window.location.href = CALENDLY_URL;
    }
  };

  const optionBtn = (active: boolean) =>
    `w-full text-left rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
      active
        ? "border-accent bg-accent/10 text-foreground"
        : "border-border bg-background hover:border-accent/50 hover:bg-secondary/50 text-foreground/80"
    }`;

  return (
    <div className="rounded-2xl border border-border bg-card shadow-elegant overflow-hidden">
      <div className="bg-primary text-white px-6 py-5">
        <div className="font-display text-lg md:text-xl font-bold">
          Dein Förder-Potenzial in 60 Sekunden
        </div>
        <div className="mt-2 flex items-center gap-2">
          {[1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-accent" : "bg-white/20"}`}
            />
          ))}
        </div>
        <div className="mt-2 text-xs text-white/70">Schritt {Math.min(step, 3)} von 3</div>
      </div>

      <div className="p-6">
        {sent ? (
          <div className="text-center py-6">
            <div className="font-display text-xl font-bold text-foreground">Danke!</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Wir melden uns kurzfristig bei dir. Du kannst dir auch direkt einen Termin sichern.
            </p>
            <Button asChild variant="hero" size="lg" className="mt-5 w-full">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Termin buchen <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {step === 1 && (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Wie sparst du aktuell fürs Alter?
                </p>
                <div className="space-y-2">
                  {savingOptions.map((o) => (
                    <button
                      key={o}
                      type="button"
                      className={optionBtn(saving === o)}
                      onClick={() => {
                        setSaving(o);
                        setStep(2);
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Wie viel legst du monatlich zurück?
                </p>
                <div className="space-y-2">
                  {rateOptions.map((o) => (
                    <button
                      key={o}
                      type="button"
                      className={optionBtn(rate === o)}
                      onClick={() => {
                        setRate(o);
                        setStep(3);
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs text-muted-foreground hover:text-accent"
                >
                  ← zurück
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Wohin dürfen wir dein Ergebnis schicken?
                </p>
                <input
                  required
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent"
                />
                <input
                  required
                  type="email"
                  maxLength={255}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-Mail"
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent"
                />
                <input
                  required
                  type="tel"
                  maxLength={40}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Telefon"
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent"
                />
                <Button type="submit" variant="hero" size="lg" className="w-full" disabled={busy}>
                  Kostenfreie Analyse anfordern <ArrowRight className="w-4 h-4" />
                </Button>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Mit dem Absenden stimmst du der Kontaktaufnahme zu. Details in unserer{" "}
                  <Link to="/datenschutz" className="underline hover:text-accent">
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-xs text-muted-foreground hover:text-accent"
                >
                  ← zurück
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

function Stars({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="w-4 h-4 text-accent fill-current" />
      ))}
    </span>
  );
}

/* ---------------------------------- page ---------------------------------- */

export default function AltersvorsorgeAnalyse() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEO
        title="Altersvorsorge-Analyse – FINABLE"
        description="Kostenfreie Analyse: Wir rechnen dir aus, welche Steuervorteile und Förderungen für deine Altersvorsorge in Frage kommen."
        canonical="https://finable.de/deine-foerderanalyse"
        noindex
        jsonLd={faqSchema}
      />

      {/* 1. HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 bg-primary/90 backdrop-blur-md border-b border-white/10">
        <div className="container flex items-center h-16 md:h-20 gap-6">
          <Link to="/" className="flex items-center mr-auto" aria-label="Finable Startseite">
            <img src={logoWhite} alt="Finable – Unabhängige Finanzberatung Logo" width={120} height={36} className="h-8 md:h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {anchors.map((a) => (
              <a key={a.href} href={a.href} className="text-sm font-medium text-white/80 hover:text-white transition-colors">
                {a.label}
              </a>
            ))}
          </nav>
          <Button asChild variant="hero" size="default">
            <a href="#analyse-formular">Termin buchen</a>
          </Button>
        </div>
      </header>

      <main>
        {/* 2. HERO */}
        <section className="relative overflow-hidden bg-gradient-hero text-white pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
          <div className="container relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium text-white/80 mb-6">
                Für Arbeitnehmer und Angestellte
              </div>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1] text-balance">
                Bis zu <span className="text-accent">7.500 € Steuervorteile und Förderungen</span> für deine Altersvorsorge — die meisten lassen dieses Geld liegen.
              </h1>
              <p className="mt-6 text-lg md:text-xl font-medium text-white/90">
                Wir rechnen dir in 15 Minuten aus, was dir zusteht.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Individuelle Berechnung auf Basis deiner echten Zahlen",
                  "Kostenfrei und unverbindlich — im Erstgespräch wird nichts verkauft",
                  "Unabhängige Beratung (§ 34d / § 34f GewO)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white/85">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-accent" />
                    </span>
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div id="analyse-formular" className="scroll-mt-28">
              <LeadForm />
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/80">
                <Stars />
                <span>
                  <strong className="text-white">4,7/5</strong> bei Trustpilot · 127 Bewertungen
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* 3. TRUST-LEISTE */}
        <section className="border-b border-border bg-secondary/40">
          <div className="container py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-foreground/80">
            <span className="flex items-center gap-2"><Stars /> Trustpilot 4,7/5 (127 Bewertungen)</span>
            <span className="flex items-center gap-2"><Stars /> Google-Bewertungen</span>
            <span>500+ zufriedene Kunden</span>
            <span>2.400+ betreute Verträge</span>
            <span>100 % unabhängige Beratung</span>
          </div>
        </section>

        {/* 4. DAS PROBLEM */}
        <section id="das-problem" className="scroll-mt-24 bg-primary text-white py-20 md:py-28">
          <div className="container">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance max-w-2xl">
              Die Ausgangslage
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              Vier Punkte, die für fast jeden Arbeitnehmer gelten — unabhängig davon, wie viel er
              heute schon zurücklegt.
            </p>
            <div className="mt-12 grid sm:grid-cols-2 gap-6">
              {problems.map((p) => (
                <div
                  key={p.n}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7 hover:border-accent/40 hover:bg-white/[0.07] transition-all"
                >
                  <div className="font-display text-2xl font-extrabold text-accent">{p.n}</div>
                  <h3 className="mt-3 font-display text-xl font-bold">{p.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-white/75 leading-relaxed">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. DIE RECHNUNG */}
        <section id="die-rechnung" className="scroll-mt-24 py-20 md:py-28">
          <div className="container">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-center text-balance">
              Gleiche Rate. <span className="text-accent">88.000 € Unterschied.</span>
            </h2>

            <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="rounded-2xl border border-border bg-card p-8">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Weg A — ohne Förderung
                </div>
                <div className="mt-4 font-display text-2xl font-bold text-foreground">
                  200 € monatlich aus dem Netto
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-sm text-muted-foreground">Ergebnis nach 40 Jahren</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-extrabold text-foreground">
                    210.000 €
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border-2 border-accent bg-card p-8 relative shadow-elegant">
                <span className="absolute -top-3 right-6 rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-foreground">
                  +88.000 €
                </span>
                <div className="text-xs font-bold uppercase tracking-wider text-accent">
                  Weg B — mit Förderung &amp; Steuervorteilen
                </div>
                <div className="mt-4 font-display text-2xl font-bold text-foreground">
                  200 € Rate + bis zu 84 € Förderung/Steuervorteil pro Monat
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-sm text-muted-foreground">Ergebnis nach 40 Jahren</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-extrabold text-accent">
                    298.000 €
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-8 text-xs text-muted-foreground text-center max-w-3xl mx-auto leading-relaxed">
              Vereinfachte Modellrechnung zur Veranschaulichung, individuelle Ergebnisse abhängig von
              Einkommen, Familienstand und Produktwahl. Förderhöhe „bis zu“.
            </p>
          </div>
        </section>

        {/* 6. SO LÄUFT ES AB */}
        <section className="bg-secondary/40 py-20 md:py-28">
          <div className="container">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-center text-balance">
              So läuft es ab
            </h2>
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {steps.map((s) => (
                <div key={s.n} className="rounded-2xl border border-border bg-card p-8">
                  <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center font-display font-extrabold text-accent">
                    {s.n}
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. KUNDENSTIMMEN */}
        <section className="py-20 md:py-28">
          <div className="container">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-center text-balance">
              Was unsere Mandant:innen sagen
            </h2>

            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {quotes.map((t) => (
                <figure key={t.name} className="rounded-2xl border border-border bg-card p-7">
                  <Stars />
                  <blockquote className="mt-4 text-sm md:text-base text-foreground/85 leading-relaxed">
                    „{t.quote}“
                  </blockquote>
                  <figcaption className="mt-5 text-sm">
                    <span className="font-semibold text-foreground">
                      {t.name}, {t.age}
                    </span>
                    <span className="block text-muted-foreground">
                      {t.profession} · {t.city}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-6 items-start">
              {videos.map((id) => (
                <ConsentGate key={id} service="Vimeo">
                  <iframe
                    src={`https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0`}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Vimeo Kundenstimme ${id}`}
                  />
                </ConsentGate>
              ))}
              <div className="rounded-2xl border border-border bg-card p-7 text-center">
                <Stars className="justify-center" />
                <div className="mt-3 font-display text-2xl font-extrabold text-foreground">4,7/5</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  127 Bewertungen bei Trustpilot
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <Play className="w-3.5 h-3.5" /> Videos laden erst nach Cookie-Zustimmung
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. FAQ */}
        <section id="faq" className="scroll-mt-24 bg-secondary/40 py-20 md:py-28">
          <div className="container max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-center text-balance">
              Häufige Fragen
            </h2>
            <Accordion type="single" collapsible className="mt-10">
              {faqs.map((f) => (
                <AccordionItem key={f.q} value={f.q}>
                  <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* 9. ABSCHLUSS-CTA */}
        <section className="bg-gradient-hero text-white py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative text-center">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance">
              Rechne aus, was dir zusteht.
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild variant="hero" size="xl">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Termin buchen <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button asChild variant="outlineLight" size="xl">
                <a href="#analyse-formular">oder Formular ausfüllen</a>
              </Button>
            </div>
            <p className="mt-8 text-xs text-white/60 max-w-2xl mx-auto">
              Allgemeine Information, ersetzt keine individuelle Beratung.
            </p>
          </div>
        </section>
      </main>

      {/* 10. FOOTER */}
      <Footer />
    </div>
  );
}
